import { useContext } from "react"

import { QueryClientContext } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"

import { Lock } from "@/components/icons"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"

import { RequestMoreDialog } from "./request-more-dialog"
import { UsagePopover } from "./usage-popover"
import { useFairUseStore } from "../fair-use-store"
import {
  clampPercentForDisplay,
  getUsageColorBand,
  roundPercent,
  usageBandTextClass,
} from "../fair-use-utils"
import { useMyUsage } from "../hooks/use-my-usage"
import { useFairUsePlanConfig } from "../hooks/use-plan-entitlements"

const RING_RADIUS = 10
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS

type UsageIndicatorProps = {
  className?: string
}

/**
 * AI-budget indicator for the chat composer toolbar (spec section 7): the
 * user's plan name as a muted label next to a progress ring colored by band
 * (green < 50, yellow 50–75, orange 75–90, red >= 90), with a lock glyph in
 * the ring and a "Paused" suffix on the label when the user is hard-blocked.
 * Modelled on the model/effort/usage readout at the right end of the Claude
 * composer. Hidden entirely while loading, on error, and when fair use is
 * disabled for the environment. Clicking it opens the usage popover; a
 * `fair_use_blocked` WS error opens it automatically via the fair-use store.
 */
export function UsageIndicator({ className }: UsageIndicatorProps = {}) {
  // The composer renders in a few standalone harnesses (component tests,
  // isolated panels) that provide no QueryClientProvider — degrade to hidden
  // there instead of letting useQuery throw and take the composer down.
  const queryClient = useContext(QueryClientContext)
  if (!queryClient) {
    return null
  }
  return <UsageIndicatorContent className={className} />
}

function UsageIndicatorContent({ className }: UsageIndicatorProps) {
  const { t } = useTranslation()
  const { data: usage } = useMyUsage()
  const fairUseConfig = useFairUsePlanConfig()
  const popoverOpen = useFairUseStore((state) => state.popoverOpen)
  const setPopoverOpen = useFairUseStore((state) => state.setPopoverOpen)

  if (!usage?.enabled) {
    return null
  }

  const percent = roundPercent(usage.percent)
  const band = getUsageColorBand(usage.percent, usage.status)
  const isBlocked = usage.status === "block"
  const sweep = clampPercentForDisplay(usage.percent)
  const ariaLabel = isBlocked
    ? t("fairUse.indicator.ariaLabelBlocked", { percent })
    : t("fairUse.indicator.ariaLabel", { percent })
  // Same two sources, same priority, as `PlanSummary` in the popover: the
  // per-user usage row first, then the tenant-wide `fair_use` config block.
  const planId = usage.tier ?? fairUseConfig.plan ?? undefined
  const planName = planId ? t(`fairUse.plans.${planId}`, { defaultValue: planId }) : null
  const planLabel =
    planName && isBlocked ? `${planName} · ${t("fairUse.popover.blockedBadge")}` : planName

  return (
    <>
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <button
            aria-label={planLabel ? `${planLabel}. ${ariaLabel}` : ariaLabel}
            data-testid="fair-use-indicator"
            type="button"
            className={cn(
              "hover:bg-accent focus-visible:ring-ring flex h-7 shrink-0 cursor-pointer items-center gap-1.5 rounded-md px-1.5 transition-colors focus-visible:ring-1 focus-visible:outline-none",
              className
            )}
            onMouseDown={(e) => {
              // Match the sibling composer buttons: keep the textarea focused
              // so the options row does not collapse mid-click.
              e.preventDefault()
            }}
          >
            {planLabel && (
              <span
                data-testid="fair-use-indicator-plan"
                className={cn(
                  "text-muted-foreground hidden max-w-[10rem] truncate text-xs sm:inline",
                  isBlocked && usageBandTextClass(band)
                )}
              >
                {planLabel}
              </span>
            )}
            <span
              className={cn(
                "relative flex size-5 shrink-0 items-center justify-center",
                usageBandTextClass(band)
              )}
            >
              <svg aria-hidden="true" className="size-5 -rotate-90" viewBox="0 0 24 24">
                <circle
                  className="stroke-muted"
                  cx="12"
                  cy="12"
                  fill="none"
                  r={RING_RADIUS}
                  strokeWidth="3.5"
                />
                <circle
                  className="stroke-current transition-[stroke-dashoffset] duration-300"
                  cx="12"
                  cy="12"
                  fill="none"
                  r={RING_RADIUS}
                  strokeDasharray={RING_CIRCUMFERENCE}
                  strokeDashoffset={RING_CIRCUMFERENCE * (1 - sweep / 100)}
                  strokeLinecap="round"
                  strokeWidth={band === "red" ? "4" : "3.5"}
                />
              </svg>
              {isBlocked && (
                <Lock aria-hidden="true" className="absolute size-2.5" strokeWidth={3} />
              )}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80" side="top" sideOffset={8}>
          <UsagePopover usage={usage} />
        </PopoverContent>
      </Popover>
      {/* Mounted beside (not inside) the popover: opening the modal dialog
          dismisses the popover as an outside interaction, which would unmount
          any dialog rendered within the popover content. */}
      <RequestMoreDialog usage={usage} />
    </>
  )
}
